import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  createTextTransform,
  TextTransformType,
} from "../actions/text-transform/createTextTransform";
import { getJobOutputText } from "../actions/jobs/getJobOutputText";
import { getAuthToken } from "../utils/getAuthToken";
import { getCurrentUserId } from "../utils/getCurrentUserId";
import { useRealtimeJob } from "./useRealtimeJob";
import type { JobStatusUpdate } from "./useAppSyncSubscription";

interface UseTransformOptions {
  initialText?: string;
  additionalContext?: string;
}

interface UseTransformResult {
  originalText: string;
  displayText: string;
  isTransforming: boolean;
  isStreamingResult: boolean;
  isSourceStreaming: boolean;
  transformError: string;
  transformJobId: string | null;
  transformDownloadKey: string;
  hasText: boolean;
  setSourceText: (text: string) => void;
  /**
   * Like setSourceText, but turns on the typewriter reveal in the
   * display panel. Use when transcription / extraction completes — the
   * full text arrives in one chunk but appears to "type out" via
   * AnimatedStreamingText. Same pattern as legal-opinion-frontend's
   * ThinkingSection. Cursor + reveal cap at SOURCE_STREAMING_MS so
   * long transcripts don't take minutes to settle.
   */
  streamSourceText: (text: string) => void;
  setDisplayText: (text: string) => void;
  runTransform: (transformType: TextTransformType, customInstruction?: string) => Promise<void>;
  reset: () => void;
  clearError: () => void;
}

/**
 * How long the typewriter reveal animation runs before isSourceStreaming
 * auto-flips off. AnimatedStreamingText ticks at 35ms / 5 chars-per-tick,
 * which is ~6 minutes for a 50KB transcript. Capping at 4s keeps the
 * "appearing as typed" effect for short transcripts AND prevents a long
 * audio job from leaving the cursor blinking forever.
 */
const SOURCE_STREAMING_MS = 4000;

export const useTransform = (
  options: UseTransformOptions = {},
): UseTransformResult => {
  const [originalText, setOriginalText] = useState(options.initialText || "");
  const [displayText, setDisplayText] = useState(options.initialText || "");
  const [isTransforming, setIsTransforming] = useState(false);
  const [isStreamingResult, setIsStreamingResult] = useState(false);
  const [isSourceStreaming, setIsSourceStreaming] = useState(false);
  const [transformError, setTransformError] = useState("");
  const [transformJobId, setTransformJobId] = useState<string | null>(null);
  const [transformDownloadKey, setTransformDownloadKey] = useState("");
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const activeRequestRef = useRef(0);
  const hasStreamingChunkRef = useRef(false);
  const transformJobIdRef = useRef<string | null>(null);
  const sourceStreamingTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    transformJobIdRef.current = transformJobId;
  }, [transformJobId]);

  useEffect(() => {
    let mounted = true;

    const loadCurrentUserId = async () => {
      const userId = await getCurrentUserId();
      if (mounted) {
        setCurrentUserId(userId);
      }
    };

    void loadCurrentUserId();

    return () => {
      mounted = false;
    };
  }, []);

  const handleRealtimeStatusUpdate = useCallback((update: JobStatusUpdate) => {
    if (!transformJobIdRef.current || update.jobId !== transformJobIdRef.current) {
      return;
    }

    if (update.isStreamingUpdate && update.streamingContent) {
      setIsStreamingResult(true);
      setDisplayText((prev) => {
        if (!hasStreamingChunkRef.current) {
          hasStreamingChunkRef.current = true;
          return update.streamingContent!;
        }

        return prev + update.streamingContent!;
      });
    }
  }, []);

  const handleTransformCompleted = useCallback(
    async ({ jobId }: { jobId: string }) => {
      if (!transformJobIdRef.current || jobId !== transformJobIdRef.current) {
        setIsTransforming(false);
        hasStreamingChunkRef.current = false;
        setIsStreamingResult(false);
        return;
      }

      try {
        const requestId = activeRequestRef.current;
        const result = await getJobOutputText(jobId);

        if (activeRequestRef.current !== requestId || jobId !== transformJobIdRef.current) {
          setIsTransforming(false);
          hasStreamingChunkRef.current = false;
          setIsStreamingResult(false);
          return;
        }

        if (!result.text) {
          setTransformError("Transform completed without output.");
          setIsTransforming(false);
          hasStreamingChunkRef.current = false;
          setIsStreamingResult(false);
          return;
        }

        setDisplayText(result.text);
        // Download the REAL rendered document (outputKey), not the internal `.txt` content
        // cache. The old `contentTextKey.replace(".txt", ".docx")` produced an
        // `internal-text-cache/...docx` key that (a) doesn't exist and (b) the download
        // guard rejects as outside the user's service namespace → 403 "Download failed"
        // (Trello #404). `outputKey` is `text-transform/<userId>/text_transform_<jobId>.docx`.
        if (result.outputKey) {
          setTransformDownloadKey(result.outputKey);
        }
        hasStreamingChunkRef.current = false;
        setIsStreamingResult(false);
        setIsTransforming(false);
      } catch (error) {
        setTransformError(error instanceof Error ? error.message : "Transform failed.");
        setIsTransforming(false);
        hasStreamingChunkRef.current = false;
        setIsStreamingResult(false);
      }
    },
    [],
  );

  const handleTransformFailed = useCallback(
    async ({ jobId, error }: { jobId: string; error: string }) => {
      if (!transformJobIdRef.current || jobId !== transformJobIdRef.current) {
        return;
      }

      hasStreamingChunkRef.current = false;
      setIsStreamingResult(false);
      setTransformError(error || "Transform failed.");
      setIsTransforming(false);
    },
    [],
  );

  useRealtimeJob({
    jobId: transformJobId,
    userId: currentUserId,
    getAuthToken,
    enabled: Boolean(transformJobId && currentUserId && isTransforming),
    onUpdate: handleRealtimeStatusUpdate,
    onCompleted: handleTransformCompleted,
    onFailed: handleTransformFailed,
  });

  const clearSourceStreamingTimeout = useCallback(() => {
    if (sourceStreamingTimeoutRef.current !== null) {
      window.clearTimeout(sourceStreamingTimeoutRef.current);
      sourceStreamingTimeoutRef.current = null;
    }
  }, []);

  /**
   * #611 — supersede any in-flight transform request.
   *
   * Every reset path below clears `isTransforming` and `transformJobId`, which
   * disables the realtime subscription (see its `enabled` flag). But an
   * in-flight `createTextTransform()` call is still running, and when it
   * resolves it sets `transformJobId` again — its only guard is the requestId.
   * Without bumping that counter first, the job id lands while
   * `isTransforming` is false, so nothing ever subscribes to it and the
   * transform is silently ORPHANED: the server-side job still runs and still
   * bills, the user sees no result, and clicks again.
   *
   * Bumping the counter makes the late response a no-op, which is what "the
   * source changed underneath it" should mean.
   */
  const invalidateInFlightTransform = useCallback(() => {
    activeRequestRef.current += 1;
  }, []);

  const setSourceText = useCallback((text: string) => {
    clearSourceStreamingTimeout();
    invalidateInFlightTransform();
    setOriginalText(text);
    setDisplayText(text);
    setIsTransforming(false);
    hasStreamingChunkRef.current = false;
    setIsStreamingResult(false);
    setIsSourceStreaming(false);
    setTransformError("");
    setTransformJobId(null);
    setTransformDownloadKey("");
  }, [clearSourceStreamingTimeout, invalidateInFlightTransform]);

  const streamSourceText = useCallback((text: string) => {
    clearSourceStreamingTimeout();
    invalidateInFlightTransform();
    setOriginalText(text);
    setDisplayText(text);
    setIsTransforming(false);
    hasStreamingChunkRef.current = false;
    setIsStreamingResult(false);
    setTransformError("");
    setTransformJobId(null);
    setTransformDownloadKey("");
    // Turn on the typewriter reveal. AnimatedStreamingText starts from
    // visibleLength=0 because the panel was mounted with empty text, then
    // ticks forward at 35ms / 5 chars-per-tick. Cap at SOURCE_STREAMING_MS
    // so long transcripts don't keep the cursor blinking for minutes.
    setIsSourceStreaming(true);
    sourceStreamingTimeoutRef.current = window.setTimeout(() => {
      setIsSourceStreaming(false);
      sourceStreamingTimeoutRef.current = null;
    }, SOURCE_STREAMING_MS);
  }, [clearSourceStreamingTimeout, invalidateInFlightTransform]);

  useEffect(() => {
    return () => {
      clearSourceStreamingTimeout();
    };
  }, [clearSourceStreamingTimeout]);

  const clearError = useCallback(() => {
    setTransformError("");
  }, []);

  const reset = useCallback(() => {
    clearSourceStreamingTimeout();
    invalidateInFlightTransform();
    setDisplayText(originalText);
    setIsTransforming(false);
    hasStreamingChunkRef.current = false;
    setIsStreamingResult(false);
    setIsSourceStreaming(false);
    setTransformError("");
    setTransformJobId(null);
    setTransformDownloadKey("");
  }, [originalText, clearSourceStreamingTimeout, invalidateInFlightTransform]);

  const runTransform = useCallback(
    async (transformType: TextTransformType, customInstruction?: string) => {
      const sourceText = originalText.trim() || displayText.trim();
      if (!sourceText) {
        setTransformError("No text available to transform.");
        return;
      }

      setIsTransforming(true);
      hasStreamingChunkRef.current = false;
      setIsStreamingResult(false);
      setTransformError("");
      const requestId = activeRequestRef.current + 1;
      activeRequestRef.current = requestId;

      try {
        const response = await createTextTransform([], {
          sourceText,
          transformType,
          customInstruction,
          additionalContext: options.additionalContext,
        });
        if (activeRequestRef.current !== requestId) {
          return;
        }

        setTransformJobId(response.jobId);
      } catch (error: any) {
        if (activeRequestRef.current === requestId) {
          hasStreamingChunkRef.current = false;
          setIsStreamingResult(false);
          const serverMessage = error?.response?.data?.message;
          setTransformError(serverMessage || (error instanceof Error ? error.message : "Transform failed."));
          setIsTransforming(false);
        }
      }
    },
    [originalText, displayText, options.additionalContext],
  );

  const hasText = useMemo(() => !!displayText.trim() || !!originalText.trim(), [displayText, originalText]);

  return {
    originalText,
    displayText,
    isTransforming,
    isStreamingResult,
    isSourceStreaming,
    transformError,
    transformJobId,
    transformDownloadKey,
    hasText,
    setSourceText,
    streamSourceText,
    setDisplayText,
    runTransform,
    reset,
    clearError,
  };
};
